"use client";

import { Link, useLocation } from "react-router-dom";
import { useTranslations } from "@/i18n/useTranslations";
import { ChevronRight, Home } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildBreadcrumbs, stripLocale, type BreadcrumbItem } from "@/lib/navigation-config";

export interface BreadcrumbProps {
  className?: string;
  /**
   * Override the breadcrumb items. If not provided, automatically built from current path.
   */
  items?: BreadcrumbItem[];
  /**
   * Optional params for resolving dynamic route labels (e.g., { name: "Competition Name" })
   */
  params?: Record<string, string>;
}

export function Breadcrumb({ className, items, params }: BreadcrumbProps) {
  const location = useLocation();
  const t = useTranslations();
  const tCommon = useTranslations("common");

  // Build breadcrumbs from current path if not provided
  const breadcrumbs = items || buildBreadcrumbs(stripLocale(location.pathname));

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav
      className={cn(
        "flex items-center gap-1 text-sm overflow-x-auto",
        "scrollbar-hide",
        className
      )}
      aria-label={tCommon("breadcrumb")}
    >
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const label = item.labelKey ? t(item.labelKey) : item.path;
        const href = item.path;

        return (
          <div key={href + index} className="flex items-center gap-1 shrink-0">
            {index === 0 ? (
              <Link
                to={href}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded-md",
                  "text-muted-foreground hover:text-foreground",
                  "transition-colors duration-200",
                  "hover:bg-overlay/50"
                )}
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            ) : (
              <>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                {isLast ? (
                  <span className="font-medium text-foreground">
                    {label}
                  </span>
                ) : (
                  <Link
                    to={href}
                    className={cn(
                      "px-2 py-1 rounded-md",
                      "text-muted-foreground hover:text-foreground",
                      "transition-colors duration-200",
                      "hover:bg-overlay/50"
                    )}
                  >
                    {label}
                  </Link>
                )}
              </>
            )}
          </div>
        );
      })}
    </nav>
  );
}
